import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Lock,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import api from "../lib/api";
import { storeToken, storeUserMetadata } from "../lib/auth";

const AdminLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setMounted(true);
  }, []);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Admin email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const res = await api.post("/api/auth/login", {
        email: formData.email,
        password: formData.password,
      });

      const userRole = res.data?.user?.role || "regularUser";
      if (userRole !== "admin" && userRole !== "superAdmin") {
        setErrors({
          email: `Access denied: Admins only. (Current role: ${userRole})`,
        });
        setIsLoading(false);
        return;
      }

      storeToken(res.data.token);
      storeUserMetadata(res.data.user);

      navigate("/admin");
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Authentication failed. Invalid admin credentials.";
      setErrors({ email: errorMsg });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundImage: "url(./images/login-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-950 to-emerald-950 flex items-center justify-center p-4 overflow-hidden relative"
    >
      {/* Background Glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-[#1A8F98]/20 rounded-full blur-3xl pointer-events-none" />

      {/* Main card */}
      <div
        className={`w-full max-w-[480px] relative z-10 transition-all duration-500 backdrop-blur-md bg-black/40 rounded-3xl border border-white/20 shadow-2xl ${
          mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="p-8 sm:p-10">
          {/* Logo and header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[#2A9D8F] to-emerald-600 rounded-2xl mb-4 shadow-lg shadow-teal-900/40">
              <ShieldCheck size={30} className="text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1.5">
              Sync & Explore
            </h1>
            <p className="text-teal-200/90 text-sm font-medium">
              Administrator Login Portal
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-white text-xs font-semibold uppercase tracking-wider mb-2">
                Admin Email
              </label>
              <div className="relative group">
                <img
                  src="/images/login-mail-icon.svg"
                  alt="Mail"
                  className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your admin email"
                  className="w-full bg-white/10 backdrop-blur-sm text-white placeholder-gray-400 rounded-xl py-3.5 pl-12 pr-4 text-sm border border-white/20 focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/30 hover:bg-white/15 transition-all duration-300"
                />
              </div>
              {errors.email && (
                <p className="text-red-400 text-xs mt-1.5 font-medium animate-pulse">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-white text-xs font-semibold uppercase tracking-wider mb-2">
                Password
              </label>
              <div className="relative group">
                <Lock
                  className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-300 opacity-80 group-hover:opacity-100"
                  size={19}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full bg-white/10 backdrop-blur-sm text-white placeholder-gray-400 rounded-xl py-3.5 pl-12 pr-11 text-sm border border-white/20 focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/30 hover:bg-white/15 transition-all duration-300"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-300 hover:text-white transition-colors"
                >
                  {showPassword ? (
                    <Eye className="w-4 h-4" />
                  ) : (
                    <EyeOff className="w-4 h-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-400 text-xs mt-1.5 font-medium">
                  {errors.password}
                </p>
              )}
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full text-white bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1d6b63] py-3.5 rounded-xl font-bold text-sm tracking-wide shadow-lg shadow-teal-950/50 hover:scale-[1.01] active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer mt-6"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Authenticating...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Access Admin Dashboard
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </button>
          </form>

          {/* Back to main site link */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate("/login")}
              className="text-xs font-semibold text-teal-300 hover:text-white transition-colors"
            >
              ← Back to User Login
            </button>
          </div>
        </div>

        {/* Footer info */}
        <div className="text-center pb-6 text-slate-400 text-[11px] font-medium border-t border-white/10 pt-4">
          <p>© 2026 Sync and Explore. Authorized Administrative Access Only.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
